import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '~/contexts';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';
import { BackLink, EmptyState, Mono, Table, Td, Th } from '../ai/replay/shared';
import WorkspaceDisplay from '../ai/replay/WorkspaceDisplay';
import ActorCell from '../traces/ActorCell';
import { buildDiffs } from '../traces/traceUtils';

const HeaderTable = styled(Table)`
  margin-bottom: 1.5rem;
`;

const Type = styled.span`
  font-size: 0.8125rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
  margin: 1.5rem 0 0.75rem;
`;

const STATUS_COLORS = {
  succeeded: colors.success,
  failed: colors.danger,
  running: colors.warning,
  pending: colors.grey40,
};

const StatusBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $status }) => STATUS_COLORS[$status] || colors.grey40};
  background: ${({ $status }) => (STATUS_COLORS[$status] || colors.grey40) + '15'};
  padding: 0.125rem 0.5rem;
  border-radius: 0.125rem;
`;

const KIND_COLORS = {
  created: colors.success,
  updated: colors.warning,
  deleted: colors.danger,
};

const KindBadge = styled.span`
  font-size: 0.625rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $kind }) => KIND_COLORS[$kind] || colors.grey40};
  background: ${({ $kind }) => (KIND_COLORS[$kind] || colors.grey40) + '15'};
  padding: 0.0625rem 0.375rem;
  border-radius: 0.125rem;
`;

const Touch = styled.div`
  border: solid 1px ${colors.grey10};
  border-radius: 0.375rem;
  overflow: hidden;

  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;

const TouchHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: ${colors.grey5};
  border-bottom: solid 1px ${colors.grey10};
`;

const TraceLink = styled(Link)`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.8125rem;
  font-weight: ${weights.medium};
  color: ${colors.black};

  &:hover {
    color: ${colors.primary};
  }
`;

const Performer = styled.div`
  margin-left: auto;
`;

const Time = styled.span`
  font-size: 0.6875rem;
  color: ${colors.grey40};
`;

const DiffTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const DiffTh = styled.th`
  text-align: left;
  padding: 0.5rem 0.75rem;
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  color: ${colors.grey55};
  text-transform: uppercase;
  border-bottom: solid 1px ${colors.grey10};
  width: ${({ $width }) => $width || 'auto'};
`;

const DiffTd = styled.td`
  padding: 0.5rem 0.75rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.75rem;
  vertical-align: top;
  border-bottom: solid 1px ${colors.grey10};
  word-break: break-word;
  white-space: pre-wrap;
  color: ${({ $tone }) => ($tone === 'before' ? colors.danger : $tone === 'after' ? colors.success : colors.black)};
`;

const Unchanged = styled.span`
  color: ${colors.grey40};
`;

const NoChanges = styled.div`
  padding: 0.625rem 0.75rem;
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

function formatValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// One trace's snapshot events for this entity collapse to a single diff.
function touchDiff(touch) {
  return buildDiffs(touch.events)[0] ?? { kind: 'updated', changes: [] };
}

export default function EntityDetail() {
  const { entity, entityId } = useParams();
  const api = useApi();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchEntity = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data: result } = await api.admin.entityReplay.getEntity(entity, entityId);
        if (!cancelled) setData(result);
      } catch (error) {
        if (!cancelled && error.status !== 404) throw error;
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchEntity();
    return () => {
      cancelled = true;
    };
  }, [api, entity, entityId]);

  const touches = useMemo(
    () => (data?.touches ? data.touches.map((touch) => ({ touch, diff: touchDiff(touch) })) : []),
    [data],
  );

  return (
    <>
      {isLoading && <EmptyState>Loading entity...</EmptyState>}

      {!isLoading && !data && <EmptyState>Entity not found.</EmptyState>}

      {!isLoading && data && (
        <>
          <BackLink as={Link} to="/admin/entities">
            <Icon icon="arrow-left" />
            Back to entities
          </BackLink>

          <HeaderTable>
            <thead>
              <tr>
                <Th>Entity</Th>
                <Th>Workspace</Th>
                <Th>Traces</Th>
                <Th>First seen</Th>
                <Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>
                  <Type>{data.entity}</Type>
                  <div>
                    <Mono>{data.entityId}</Mono>
                  </div>
                </Td>
                <Td>
                  <WorkspaceDisplay workspace={data.workspace} />
                </Td>
                <Td>{data.traceCount}</Td>
                <Td title={data.firstSeen}>{moment(data.firstSeen).format('MMM D, YYYY h:mm a')}</Td>
                <Td title={data.lastSeen}>{moment(data.lastSeen).format('MMM D, YYYY h:mm a')}</Td>
              </tr>
            </tbody>
          </HeaderTable>

          <SectionTitle>History ({touches.length})</SectionTitle>
          {touches.map(({ touch, diff }) => (
            <Touch key={touch.traceId}>
              <TouchHeader>
                <KindBadge $kind={diff.kind}>{diff.kind}</KindBadge>
                <TraceLink to={`/admin/traces/${touch.traceId}`}>{touch.source || '-'}</TraceLink>
                <StatusBadge $status={touch.status}>{touch.status}</StatusBadge>
                <Performer>
                  <ActorCell
                    actor={touch.actor}
                    member={touch.member}
                    apiKeyId={touch.apiKeyId}
                    admin={touch.admin}
                    size="1.5rem"
                  />
                </Performer>
                <Time title={touch.occurredAt}>{moment(touch.occurredAt).format('MMM D, YYYY h:mm a')}</Time>
              </TouchHeader>
              {diff.changes.length === 0 ? (
                <NoChanges>No properties changed.</NoChanges>
              ) : (
                <DiffTable>
                  <thead>
                    <tr>
                      <DiffTh $width="30%">Property</DiffTh>
                      <DiffTh $width="35%">Before</DiffTh>
                      <DiffTh $width="35%">After</DiffTh>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.changes.map((change) => (
                      <tr key={change.key} data-testid={`change-${change.key}`}>
                        <DiffTd>{change.key}</DiffTd>
                        <DiffTd $tone="before">
                          {change.before === null ? <Unchanged>—</Unchanged> : formatValue(change.before)}
                        </DiffTd>
                        <DiffTd $tone="after">
                          {change.after === null ? <Unchanged>—</Unchanged> : formatValue(change.after)}
                        </DiffTd>
                      </tr>
                    ))}
                  </tbody>
                </DiffTable>
              )}
            </Touch>
          ))}
        </>
      )}
    </>
  );
}
